import { collectCcpaChecks } from '../components/report/crosscheck';
import type { ComplianceStatus, ScanResult } from './types';

/**
 * Who a report is framed for (#2046). `consumer` is a visitor from /check,
 * checking a site they don't run. Everyone else gets the default framing.
 */
export type Audience = 'consumer';

const STORAGE_PREFIX = 'privisy_audience:';

/** The `audience` URL param, allowlisted. Anything else is no audience. */
export function parseAudience(raw: string | null): Audience | null {
  return raw === 'consumer' ? 'consumer' : null;
}

/**
 * Keep the flag for one scan in this tab, so it survives the sign-up and
 * unlock redirects that drop the URL params. Storage can be blocked; the
 * report then falls back to the default framing.
 */
export function rememberAudience(scanId: string, audience: Audience): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + scanId, audience);
  } catch {
    // Blocked storage: the flag lives only as long as the URL carries it.
  }
}

export function audienceFor(scanId: string | null | undefined): Audience | null {
  if (!scanId) return null;
  try {
    return parseAudience(window.sessionStorage.getItem(STORAGE_PREFIX + scanId));
  } catch {
    return null;
  }
}

export const CONSENT_CHECK = 'Consent Mechanism Effectiveness';
export const GPC_CHECK = 'Global Privacy Control (GPC) Signal';

/** The two answers a consumer came for. Pass/fail only, never a name. */
export interface ConsumerVerdict {
  consent: 'leaked' | 'stopped' | 'unknown';
  gpc: 'ignored' | 'honored' | 'unknown';
}

/**
 * Derived from check statuses alone. The masked payload keeps every status,
 * and `details` (which names the trackers) is never read here.
 */
export function consumerVerdict(result: ScanResult): ConsumerVerdict {
  const checks = collectCcpaChecks(result);
  const statusOf = (name: string): ComplianceStatus | null =>
    checks.find((c) => c.name === name)?.status ?? null;
  const consent = statusOf(CONSENT_CHECK);
  const gpc = statusOf(GPC_CHECK);
  return {
    consent: consent === 'non_compliant' ? 'leaked' : consent === 'compliant' ? 'stopped' : 'unknown',
    gpc: gpc === 'non_compliant' ? 'ignored' : gpc === 'compliant' ? 'honored' : 'unknown',
  };
}
